import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { researchService, ResearchRequest } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';

const NewResearch: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { credits, checkCredits } = useStripe();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const [formData, setFormData] = useState<ResearchRequest>({
    topic: '',
    depth: 3,
    focus_areas: [],
    excluded_areas: []
  });
  
  useEffect(() => {
    console.log('NewResearch component - Auth state:', { user: !!user, authLoading });
    
    // If user is not authenticated and auth loading is complete, redirect to login
    if (!user && !authLoading) {
      console.log('User not authenticated, redirecting to login from NewResearch');
      navigate('/login');
    } else if (user) {
      // Check user credits when component loads
      checkCredits();
    }
  }, [user, authLoading, navigate, checkCredits]);
  const [focusArea, setFocusArea] = useState('');
  const [excludedArea, setExcludedArea] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'depth' ? parseInt(value) : value
    });
  };

  const addFocusArea = () => {
    if (focusArea.trim() && !formData.focus_areas?.includes(focusArea.trim())) {
      setFormData({
        ...formData,
        focus_areas: [...(formData.focus_areas || []), focusArea.trim()]
      });
      setFocusArea('');
    }
  };

  const removeFocusArea = (area: string) => {
    setFormData({
      ...formData,
      focus_areas: formData.focus_areas?.filter(a => a !== area) || []
    });
  };

  const addExcludedArea = () => {
    if (excludedArea.trim() && !formData.excluded_areas?.includes(excludedArea.trim())) {
      setFormData({
        ...formData,
        excluded_areas: [...(formData.excluded_areas || []), excludedArea.trim()]
      });
      setExcludedArea('');
    }
  };

  const removeExcludedArea = (area: string) => {
    setFormData({
      ...formData,
      excluded_areas: formData.excluded_areas?.filter(a => a !== area) || []
    });
  };

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setInsufficientCredits(false);

    // Check if user is authenticated before making the API call
    if (!user) {
      console.error('User not authenticated, cannot start research');
      setError('You must be logged in to start research. Please log in and try again.');
      setIsLoading(false);
      navigate('/login');
      return;
    }
    
    // Check if user has enough research credits
    if (credits <= 0) {
      setInsufficientCredits(true);
      setIsLoading(false);
      return;
    }

    try {
      console.log('Starting research with data:', formData);
      const response = await researchService.startResearch(formData);
      console.log('Research response:', response);
      
      // Refresh credits after successful research start
      await checkCredits();
      
      if (response && response.id) {
        // Store the research ID and topic in localStorage for later notification
        const pendingResearch = {
          id: response.id,
          topic: formData.topic,
          startTime: new Date().toISOString()
        };
        
        // Get existing pending research or initialize empty array
        const existingPending = JSON.parse(localStorage.getItem('pendingResearch') || '[]');
        localStorage.setItem('pendingResearch', JSON.stringify([...existingPending, pendingResearch]));
        
        // Show success message
        setSuccessMessage(`Research on "${formData.topic}" has been started! You can continue using the app while it processes.`);
        
        // Clear form after successful submission
        setFormData({
          topic: '',
          depth: 3,
          focus_areas: [],
          excluded_areas: []
        });
        
        // Redirect to reports list after a short delay to show the success message
        setTimeout(() => {
          navigate('/reports', { 
            state: { 
              notification: `Research on "${formData.topic}" has been started! You'll be notified when it's complete.` 
            }
          });
        }, 2000);
      } else {
        setError('Failed to get research ID from server response');
        console.error('Invalid response format:', response);
      }
    } catch (err: any) {
      console.error('Research error:', err);
      
      // Check if it's an authentication error
      if (err.response?.status === 401) {
        setError('Authentication error. Please try logging in again.');
        // Redirect to login after a short delay
        setTimeout(() => navigate('/login'), 2000);
      } else {
        if (err.response && err.response.status === 402) {
          setInsufficientCredits(true);
        } else {
          setError(`Error starting research: ${err.message || 'Unknown error'}`);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white shadow-xl rounded-lg p-6 border-t-4 border-primary-900">
        <h1 className="text-2xl font-bold text-primary-900 mb-4">Start New Research</h1>
        
        {/* Marketing Banner */}
        <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
          <h2 className="text-xl font-bold text-blue-800 mb-3">Unlock the Power of AI-Driven Research</h2>
          <p className="text-blue-700 mb-3">
            Generate comprehensive, well-structured research reports in minutes instead of hours. Our AI research assistant helps you create professional-quality reports with proper citations and balanced perspectives.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3">
                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-800">For Students</h3>
                <p className="text-sm text-blue-600">Jump-start your essays and term papers with well-researched foundations and discover new perspectives.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3">
                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-800">For Executives</h3>
                <p className="text-sm text-blue-600">Make informed decisions with quick, comprehensive market and industry analyses.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3">
                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-800">For Educators</h3>
                <p className="text-sm text-blue-600">Create teaching materials and stay current on subject matter with comprehensive research summaries.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3">
                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-800">For Researchers</h3>
                <p className="text-sm text-blue-600">Accelerate literature reviews and explore new research directions with AI-assisted topic exploration.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Research Credits Status Banner */}
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${credits > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center">
            <div className={`mr-4 p-2 rounded-full ${credits > 0 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {credits > 0 ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
            </div>
            <div>
              <p className="font-medium">
                {credits > 0 
                  ? `You have ${credits} research credit${credits > 1 ? 's' : ''} available` 
                  : 'You need research credits to generate a research report'}
              </p>
              <p className="text-sm text-gray-600">Each research report requires 1 research credit</p>
            </div>
          </div>
          {credits <= 0 && (
            <Link 
              to="/payment" 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
            >
              Purchase Research Credits
            </Link>
          )}
        </div>
        
        {error && (
          <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700">
            <p>{error}</p>
          </div>
        )}
        
        {insufficientCredits && (
          <div className="mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700">
            <p className="font-bold">You need research credits to generate a research report</p>
            <p className="mt-2">Each research report requires 1 research credit. Please purchase research credits to continue.</p>
            <Link 
              to="/payment" 
              className="mt-3 inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Purchase Research Credits
            </Link>
          </div>
        )}
        
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="topic" className="block text-gray-700 font-medium mb-2">
              Research Topic
            </label>
            <input
              type="text"
              id="topic"
              name="topic"
              value={formData.topic}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-700"
              placeholder="Enter a research topic"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="depth" className="block text-gray-700 font-medium mb-2">
              Research Depth (1-5)
            </label>
            <select
              id="depth"
              name="depth"
              value={formData.depth}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-700"
            >
              <option value={1}>1 - Basic overview</option>
              <option value={2}>2 - Brief analysis</option>
              <option value={3}>3 - Standard research</option>
              <option value={4}>4 - Detailed analysis</option>
              <option value={5}>5 - Comprehensive research</option>
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">
              Focus Areas (Optional)
            </label>
            <div className="flex">
              <input
                type="text"
                value={focusArea}
                onChange={(e) => setFocusArea(e.target.value)}
                className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add a focus area"
              />
              <button
                type="button"
                onClick={addFocusArea}
                className="bg-blue-500 text-white px-4 py-2 rounded-r-md hover:bg-blue-600"
              >
                Add
              </button>
            </div>
            {formData.focus_areas && formData.focus_areas.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.focus_areas.map((area, index) => (
                  <div key={index} className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full flex items-center">
                    <span>{area}</span>
                    <button
                      type="button"
                      onClick={() => removeFocusArea(area)}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Excluded Areas (Optional)
            </label>
            <div className="flex">
              <input
                type="text"
                value={excludedArea}
                onChange={(e) => setExcludedArea(e.target.value)}
                className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add an excluded area"
              />
              <button
                type="button"
                onClick={addExcludedArea}
                className="bg-blue-500 text-white px-4 py-2 rounded-r-md hover:bg-blue-600"
              >
                Add
              </button>
            </div>
            {formData.excluded_areas && formData.excluded_areas.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.excluded_areas.map((area, index) => (
                  <div key={index} className="bg-red-100 text-red-800 px-3 py-1 rounded-full flex items-center">
                    <span>{area}</span>
                    <button
                      type="button"
                      onClick={() => removeExcludedArea(area)}
                      className="-ml-0.5 mr-1.5 h-4 w-4 text-accent-400 hover:text-red-800"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading || credits <= 0}
              className={`px-4 py-2 rounded-md text-white ${
                isLoading ? 'bg-primary-300' : 
                credits <= 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-900 hover:bg-primary-800'
              }`}
              title={credits <= 0 ? 'You need at least 1 research credit to start research' : ''}
            >
              {isLoading ? 'Starting Research...' : 
               credits <= 0 ? 'Need Research Credits to Start' : 'Start Research'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewResearch;
